import React from 'react';
import { connect } from 'react-redux';
import DataModel from '../../../../../models/DataModel';
import BaseComponent from '../../../../../baseClasses/BaseComponent';
import VideoPlayer from '../../../../../components/VideoPlayer';
import spinner from '../../../../../assets/images/loader.svg';
import Download from '../../../../../components/Download';
import CommentList from '../../../../../components/CommentList';
import FileUtil from '../../../../../helpers/FileUtil/FileUtil';
import AttachmentMetadata from '../../../../../components/AttachmentMetadata';
import AttachmentError from '../AttachmentError';
import AttachmentHelper from '../../../../../helpers/AttachmentHelper';
import { setTimeout } from 'timers';
import DataSource from '../../../../../dao/DataSource/DataSource';

import './AttachmentDisplay.scss';
import TrashIcon from '../../../../../assets/images/trash.svg';

export class AttachmentDisplay extends BaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      commentsExpanded: false,
      loading: true,
      alt: '',
    };
    
    const { currentUser } = props;
    const { primary_company_id, current_company_id } = localStorage;
    if (
      currentUser.role_name == 'super_user' ||
      (currentUser.role_name == 'admin' && primary_company_id == current_company_id)
    ) {
      this.userHasDeletePermissions = true;
    }
    
    this.deleteCloseout = this.deleteCloseout.bind(this);
    this.deleteProgressReport = this.deleteProgressReport.bind(this);
  }

  handleAttachmentRetrieval(attachment, action, acData, fetchedData) {
    if (this.attachmentDataSource) {
      if (acData && acData.type.indexOf('delete') >= 0) {
        this.setState({ attachment: null });
      }
      else if (attachment) {
        this.setState({ attachment, commentsExpanded: false });
      }
    }
  }

  handleInitializeAttachment() {
    const { attachment } = this.props;
    if (this.attachmentDataSource) {
      this.attachmentDataSource.release();
    }
    if (attachment) {
      if (attachment.group_identifier) {

        this.attachmentDataSource = new DataSource('ReferenceDocument', attachment.id,
          { reference_document: { conversions: { additionalFields: ['upload', 'attachment'] }, additionalFields: ['attachment'] } },
          this.handleAttachmentRetrieval);
        this.attachmentDataSource.get();
      }
      else if (attachment.closeout_level) {
        this.setState({ attachment: attachment });
      }
      else if (attachment.closeoutable_id && !attachment.expiring_url) {
        this.attachmentDataSource = new DataSource('Closeout', attachment.id, { closeout: { additionalFields: ['expiring_url'] } }, this.handleAttachmentRetrieval);
        this.attachmentDataSource.get();
      }
      else if (this.props.isProgressReport || attachment.progress_report_type) {
        // Progress reports have expiring_url, use directly
        this.setState({ attachment: attachment });
      }
      else {
        this.attachmentDataSource = new DataSource('Attachment', attachment.id,
          {
            attachment: {
              user: {}, conversions: {},
              additionalFields: ['attachment', 'upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'],
            },
          }, this.handleAttachmentRetrieval);
        this.attachmentDataSource.get();
      }
    }

  }

  componentDidMount(){
    this.timeout = setTimeout(() => {
      this.setState({
        loading: false,
        alt: 'File not available',
      });
    }, 5000);
    this.handleInitializeAttachment();
  }

  componentDidUpdate(prevProps) {
    const nextProps = this.props;
    if (prevProps.attachment && nextProps.attachment && nextProps.attachment.id != prevProps.attachment.id) {
      this.handleInitializeAttachment();
    }
  }

  componentWillUnmount() {
    if (this.attachmentDataSource) {
      this.attachmentDataSource.release();
      this.attachmentDataSource = null;
    }
  }

  deleteCloseout() {
    const { handleDeleteCloseout } = this.props;
    const { attachment } = this.state;
    const closeoutName = FileUtil.trimExtension(attachment.name);
    DataModel.showGlobalModal('SYSTEM_POPUP', {
      message: 'Are you sure you want to delete "'+closeoutName+'"? This action cannot be undone.',
      cancelButtonText: 'CANCEL',
      okButtonText: 'YES',
      height: 184,
      width: 320,
      handleOwnerOk: () => {
        if (handleDeleteCloseout) handleDeleteCloseout(attachment.id);
      },
    });
  }

  deleteProgressReport() {
    const { handleDeleteProgressReport } = this.props;
    const { attachment } = this.state;
    const reportName = FileUtil.trimExtension(attachment.name || 'Progress Report');
    DataModel.showGlobalModal('SYSTEM_POPUP', {
      message: 'Are you sure you want to delete "'+reportName+'"? This action cannot be undone.',
      cancelButtonText: 'CANCEL',
      okButtonText: 'YES',
      height: 184,
      width: 320,
      handleOwnerOk: () => {
        if (handleDeleteProgressReport) handleDeleteProgressReport(attachment.id);
      },
    });
  }

  handleCommentsExpand() {
    this.setState({ commentsExpanded: !this.state.commentsExpanded });
  }

  handleAssetLoaded(e){
    this.setState({ loading: false });
    clearTimeout(this.timeout);
  }

  handlePdfLoad(e){
    setTimeout(() => {
      this.setState({ loading: false });
      clearTimeout(this.timeout);
    },
    1000);
  }

  handleSeparateTimeDate(dateTime) {
    if (dateTime) {
      let d = new Date(dateTime);
      let date = (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear();

      let hours = d.getHours();
      let meridiem = hours >= 12 ? 'pm' : 'am';
      if (hours == 0) {
        hours = 12;
      }
      else if (hours > 12) {
        hours -= 12;
      }
      let minutes = d.getMinutes();
      if (minutes < 10) {
        minutes = '0' + minutes;
      }
      let time = hours + ':' + minutes + ' ' + meridiem;
      return date + '\u00A0\u00A0|\u00A0\u00A0' + time;
    }
    else {
      return '';
    }
  }

  render() {
    const { attachment } = this.state;

    if (!attachment) {
      return null;
    }
    else if (attachment.status && attachment.status != 0) {
      // closeout generation error state
      return (
        <div className='attachment-display'>
          <AttachmentError attachment={attachment}/>
        </div>
      );
    }

    let loading;
    let uploadedFile = attachment.upload ? attachment.upload : attachment.expiring_url;

    let vidWidth = window.innerWidth;
    let imageDisplay, imageMetadata = null;

    if(this.state.loading){
      loading = <img className='spinner' src={spinner} alt='spinner' />;
    }
    const isCloseout = !!attachment.closeout_level;
    const isProgressReport = this.props.isProgressReport || !!attachment.progress_report_type;
    let ext = FileUtil.getExtension(attachment.name);
    switch(ext){
    case 'png':
    case 'jpeg':
    case 'jpg':
    case 'gif':
      uploadedFile = attachment.upload_annotated;
      imageDisplay = <div className="display-image" ><img className="display-image" src={uploadedFile} alt={this.state.alt} onLoad={this.handleAssetLoaded}/></div>;
      imageMetadata = <AttachmentMetadata attachment={attachment} />;
      break;
    case 'm4v':
    case '3gp':
    case 'mp4':
    case 'mov':
      const type = uploadedFile && uploadedFile.upload_content_type ? uploadedFile.upload_content_type : (uploadedFile && uploadedFile.upload && uploadedFile.upload.type) ? uploadedFile.upload.type : undefined;
      imageDisplay = 
          <div className='display-video'>
            <VideoPlayer width={(vidWidth - 475) * .4} src={uploadedFile} onLoad={this.handleAssetLoaded} alt={this.state.alt} type={type}/>
          </div>;
      break;
    case 'pdf':
    case 'doc':
    case 'docx':
    case 'xls':
    case 'xlsm':
    case 'xlsx':
    case 'rtf':
    case 'txt':
      imageDisplay = AttachmentHelper.getConvertedDocumentView(attachment);
      break;
    default:
      let content =         
        <div>
          <div className="no-documents-icon"/>
          <div className="no-documents-text">This file type can't be previewed</div>
          <div className="no-documents-text">Click to download</div>
        </div>;

      imageDisplay = 
          <Download 
            content={content} 
            className={'non-display-file-type'}
            download={attachment.name} 
            href={uploadedFile}
          />;
      loading = null;
      break;
    }
    return (
      <div className="attachment-display" >
      {loading}
        <div className="header-container">
          <div className='caption-details'>
            <div className="attachment-name">{attachment.name}</div>
          </div>
          { 
            this.props.hasDeleteButton && isCloseout && this.userHasDeletePermissions &&
            <img className="delete-button" src={TrashIcon} alt="delete" onClick={this.deleteCloseout}/>
          }
          { 
            this.props.hasDeleteButton && isProgressReport && this.userHasDeletePermissions &&
            <img className="delete-button" src={TrashIcon} alt="delete" onClick={this.deleteProgressReport}/>
          }
        </div>
        <div className="display-container">
          {imageDisplay}
          {imageMetadata}
        </div>

        <CommentList comments={attachment.comments ? attachment.comments : []}/>

        </div>
    );
  }
}

AttachmentDisplay.defaultProps = {
  hasDeleteButton: true,
}; 

const mapStateToProps = (state) => {
  return {
    currentUser: state.new_users.currentUser,
  };
};
export default connect(mapStateToProps)(AttachmentDisplay);



// WEBPACK FOOTER //
// ./src/containers/PORContainer/PORProjectDocuments/AttachmentsTable/AttachmentDisplay/index.js